import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Load user and permissions from localStorage or refresh from API
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      if (storedUser && token) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setPermissions(userData.permissions || {});
        
        // Optionally refresh permissions from API on mount
        try {
          const response = await api.get('/auth/me');
          const freshUser = response.data;
          setUser(freshUser);
          setPermissions(freshUser.permissions || {});
          localStorage.setItem('user', JSON.stringify(freshUser));
        } catch (err) {
          // Token might be expired, keep stored data for now
          console.warn('Failed to refresh user data:', err);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setPermissions(userData.permissions || {});
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions({});
  };

  /**
   * Check if user has permission for a specific action on a page
   * @param {string} page - Page key (e.g., 'assets.servers')
   * @param {string} action - Action type: 'read', 'create', 'update', 'delete'
   * @returns {boolean}
   */
  const can = useCallback((page, action = 'read') => {
    // Admin has all permissions
    if (user?.is_admin) return true;
    
    // Check permissions matrix
    return permissions[page]?.[action] ?? false;
  }, [user, permissions]);

  /**
   * Check if user can read a page (for menu visibility)
   * @param {string} page - Page key
   * @returns {boolean}
   */
  const canRead = useCallback((page) => can(page, 'read'), [can]);

  const value = {
    user,
    permissions,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin ?? false,
    can,
    canRead,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
